import React from "react";
import { Pressable, StyleSheet, View } from "react-native";
import {
  ArrowsOutIcon,
  CheckIcon,
  XIcon,
  ArrowCounterClockwiseIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
} from "@phosphor-icons/react";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../../../components/common/general/CustomButton/CustomButton";
import { colors } from "../../../../../styles/design-system";
import { CommentPin } from "./CommentPin";
import type { CommentsApi } from "./CommentsSection";
import type { CourseEvaluatorReviewDecision, CourseEvaluatorReviewSidebarItem } from "../types/reviewTypes";

interface ReviewActionPanelProps {
  selectedReviewItem: CourseEvaluatorReviewSidebarItem | null;
  decision: CourseEvaluatorReviewDecision;
  onAccept?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onReject?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onUndo?: () => void;
  onNavigateNext?: () => void;
  onNavigatePrev?: () => void;
  canAcceptSelectedItem?: boolean;
  acceptDisabledReason?: string;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  acceptHiddenReason?: string;
  commentsApi?: CommentsApi;
  commentTargetKind?: string;
  commentTargetId?: string | null;
  commentsDisabled?: boolean;
  commentPinOpen?: boolean;
  onCommentPinOpenChange?: (isOpen: boolean) => void;
}

function ReviewActionPanel({
  selectedReviewItem,
  decision,
  onAccept,
  onReject,
  onUndo,
  onNavigateNext,
  onNavigatePrev,
  canAcceptSelectedItem = true,
  acceptDisabledReason,
  isExpanded,
  onToggleExpand,
  acceptHiddenReason,
  commentsApi,
  commentTargetKind = "review-item",
  commentTargetId,
  commentsDisabled = false,
  commentPinOpen,
  onCommentPinOpenChange,
}: ReviewActionPanelProps) {
  if (!selectedReviewItem) {
    return null;
  }

  const canUndo = !!onUndo;
  const commentPinTarget = commentsApi && commentTargetId ? { api: commentsApi, targetId: commentTargetId } : null;
  const commentsContextLabel = `Comments on ${selectedReviewItem.title}`;

  if (decision === "synced") {
    return (
      <View style={styles.bar}>
        <View style={styles.actionRow}>
          <View style={styles.leftGroup}>
            <View style={styles.publishedPill}>
              <CustomText
                text="Published"
                useTranslationText={false}
                size="xs"
                weight="bold"
                style={styles.publishedPillText}
              />
            </View>
            <CustomText
              text="Changes have been published. Published items can't be edited."
              useTranslationText={false}
              size="xs"
              style={styles.mutedText}
            />
          </View>

          <NavControls
            canUndo={canUndo}
            onUndo={onUndo}
            onNext={onNavigateNext}
            onPrev={onNavigatePrev}
            isExpanded={isExpanded}
            onToggleExpand={onToggleExpand}
          />
        </View>

        {commentPinTarget && (
          <View style={styles.commentsContainer}>
            <CommentPin
              targetKind={commentTargetKind}
              targetId={commentPinTarget.targetId}
              api={commentPinTarget.api}
              contextLabel={commentsContextLabel}
              disabled={true}
              open={commentPinOpen}
              onOpenChange={onCommentPinOpenChange}
            />
          </View>
        )}
      </View>
    );
  }

  return (
    <View style={styles.bar}>
      <View style={styles.actionRow}>
        <View style={styles.leftGroup}>
          {acceptHiddenReason ? (
            <CustomText text={acceptHiddenReason} useTranslationText={false} size="xs" style={styles.mutedText} />
          ) : (
            <CustomButton
              text="Add to Queue"
              useTranslationText={false}
              size="xs"
              styleType="primary"
              leftIcon={<CheckIcon size={14} color={colors.white} weight="bold" />}
              rightIcon={undefined}
              onPress={() => onAccept?.(selectedReviewItem)}
              disabled={!canAcceptSelectedItem}
              style={[styles.acceptButtonSize, !canAcceptSelectedItem && styles.acceptButtonDisabled]}
              textStyle={styles.acceptButtonText}
            />
          )}

          {!acceptHiddenReason && !canAcceptSelectedItem && acceptDisabledReason && (
            <CustomText text={acceptDisabledReason} useTranslationText={false} size="xs" style={styles.mutedText} />
          )}

          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Skip"
            onPress={() => onReject?.(selectedReviewItem)}
            style={styles.skipButton}>
            <XIcon size={14} color={colors.grey[700]} weight="bold" />
            <CustomText
              text="Skip"
              useTranslationText={false}
              size="xs"
              weight="medium"
              style={styles.skipButtonText}
            />
          </Pressable>
        </View>

        <NavControls
          canUndo={canUndo}
          onUndo={onUndo}
          onNext={onNavigateNext}
          onPrev={onNavigatePrev}
          isExpanded={isExpanded}
          onToggleExpand={onToggleExpand}
        />
      </View>

      {commentPinTarget && (
        <View style={styles.commentsContainer}>
          <CommentPin
            targetKind={commentTargetKind}
            targetId={commentPinTarget.targetId}
            api={commentPinTarget.api}
            contextLabel={commentsContextLabel}
            disabled={commentsDisabled}
            open={commentPinOpen}
            onOpenChange={onCommentPinOpenChange}
          />
        </View>
      )}
    </View>
  );
}

interface NavControlsProps {
  canUndo: boolean;
  onUndo?: () => void;
  onNext?: () => void;
  onPrev?: () => void;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

function NavControls({ canUndo, onUndo, onNext, onPrev, isExpanded, onToggleExpand }: NavControlsProps) {
  return (
    <View style={styles.rightGroup}>
      {onToggleExpand && !isExpanded && (
        <>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Expand review"
            onPress={onToggleExpand}
            style={styles.ghostButton}>
            <ArrowsOutIcon size={14} color={colors.grey[700]} weight="regular" />
            <CustomText
              text="Expand"
              useTranslationText={false}
              size="xs"
              weight="medium"
              style={styles.ghostButtonText}
            />
          </Pressable>
          <View style={styles.divider} />
        </>
      )}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Undo last decision"
        accessibilityState={{ disabled: !canUndo }}
        onPress={onUndo}
        disabled={!canUndo}
        style={[styles.ghostButton, !canUndo && styles.ghostButtonDisabled]}>
        <ArrowCounterClockwiseIcon size={14} color={colors.grey[700]} weight="regular" />
        <CustomText text="Undo" useTranslationText={false} size="xs" weight="medium" style={styles.ghostButtonText} />
      </Pressable>

      <View style={styles.divider} />

      <View style={styles.navGroup}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Navigate to previous item"
          onPress={onPrev}
          style={styles.kbdKey}>
          <ArrowLeftIcon size={12} color={colors.grey[700]} weight="bold" />
        </Pressable>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Navigate to next item"
          onPress={onNext}
          style={styles.kbdKey}>
          <ArrowRightIcon size={12} color={colors.grey[700]} weight="bold" />
        </Pressable>
        <CustomText text="Navigate" useTranslationText={false} size="xs" style={styles.mutedText} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  bar: {
    paddingHorizontal: 20,
    paddingTop: 8,
    paddingBottom: 12,
    backgroundColor: colors.white,
    userSelect: "none",
    gap: 10,
  },
  actionRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 26,
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    paddingBottom: 10,
  },
  commentsContainer: {
    width: "100%",
    alignItems: "flex-end",
  },
  leftGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    flex: 1,
  },
  rightGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  acceptButtonSize: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    height: 26,
  },
  acceptButtonText: {
    color: colors.white,
  },
  acceptButtonDisabled: {
    backgroundColor: colors.grey[300],
  },
  skipButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    height: 26,
  },
  skipButtonText: {
    color: colors.grey[700],
  },
  publishedPill: {
    paddingHorizontal: 10,
    height: 24,
    justifyContent: "center",
    borderRadius: 9999,
    backgroundColor: "#CCD7FF",
  },
  publishedPillText: {
    color: "#000B33",
  },
  ghostButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
  },
  ghostButtonDisabled: {
    opacity: 0.5,
  },
  ghostButtonText: {
    color: colors.grey[700],
  },
  divider: {
    width: 1,
    height: 16,
    backgroundColor: colors.grey[200],
  },
  navGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  kbdKey: {
    width: 24,
    height: 24,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 4,
  },
  mutedText: {
    color: colors.grey[500],
  },
});

export default ReviewActionPanel;
